// §11.2 — route guard for auth-required pages.
// Usage: wrap a route's Component or element with <RequireAuth />.
// If not authenticated → redirects to /login with the intended path preserved.
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, type ReactNode } from "react";
import { useSession } from "../hooks";
import { validateReturnTo } from "@/lib/catalog-pending/returnto-validator";
import { OnlinePresenceHeartbeat } from "@/features/presence";
import { preloadSocialProofAvatars } from "@/features/avatars";
import { useOptionalQueryClient } from "@/lib/query";
import { useViewerCacheBoundary } from "../use-viewer-cache-boundary";

interface RequireAuthProps {
  children?: ReactNode;
}

const LOGOUT_STORAGE_KEY = "quartel:auth:loggedOut";

export function RequireAuth({ children }: RequireAuthProps): React.JSX.Element {
  const { session, isLoading, isAuthenticated } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useOptionalQueryClient();
  const viewerCacheReady = useViewerCacheBoundary(session, queryClient);

  useLogoutBroadcastRedirect(navigate);
  useAuthenticatedAvatarPreload(isAuthenticated && session !== null);

  if (isLoading) {
    return (
      <div
        className="min-h-svh bg-background"
        aria-busy="true"
        data-skeleton="app-mobile"
      />
    );
  }

  if (!isAuthenticated || !session) {
    const from = currentReturnPath(location);
    return <Navigate to={loginUrl(from)} state={{ from }} replace />;
  }
  if (!viewerCacheReady) return <AuthenticatedAppSkeleton />;

  // Operador 09/07/2026: matrix_user é funil estrito — a conta de
  // backoffice NUNCA vê o app do usuário. Qualquer rota autenticada fora de
  // /matrix (inclusive "/" pós-login e deep links com returnTo) redireciona
  // pra lá; as superfícies /admin e /matrix têm o guard próprio no
  // AdminLayout (roleBoundary.adminGuardFor).
  if (
    session.user.role === "matrix_user" &&
    !location.pathname.startsWith("/matrix")
  ) {
    return <Navigate to="/matrix" replace />;
  }

  const queryReturnTo = new URLSearchParams(location.search).get("returnTo");
  if (queryReturnTo !== null) {
    const to = validateReturnTo(queryReturnTo, {
      currentOrigin: window.location.origin,
    });
    return <Navigate to={to} replace />;
  }

  // R16 W-B: single presence-heartbeat mount for EVERY authenticated tree
  // (mobile robot/dashboard AND the auto-selected desktop shell route both
  // pass through RequireAuth). Mounted here — not in AppShell — so public
  // pages like /login never beat, and never duplicated per layout.
  return (
    <>
      <OnlinePresenceHeartbeat />
      {children === undefined ? <Outlet /> : children}
    </>
  );
}

function AuthenticatedAppSkeleton(): React.JSX.Element {
  return (
    <div
      className="min-h-svh bg-background"
      aria-busy="true"
      data-skeleton="app-mobile"
    />
  );
}

function useAuthenticatedAvatarPreload(authenticated: boolean): void {
  useEffect(() => {
    if (!authenticated) return;
    void preloadSocialProofAvatars();
  }, [authenticated]);
}

function useLogoutBroadcastRedirect(navigate: ReturnType<typeof useNavigate>) {
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== LOGOUT_STORAGE_KEY || event.newValue === null) return;
      navigate("/login", { replace: true });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [navigate]);
}

function currentReturnPath(location: ReturnType<typeof useLocation>): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

function loginUrl(from: string): string {
  return `/login?from=${encodeURIComponent(from)}`;
}
